import React from 'react';
import cn from 'clsx';
import styles from './index.module.scss';
import Card from '@components-clean/core/card';
import { bankersRound } from '@components-clean/core/charts/shared/number-utils';

export type ReviewProgressCardProps = {
    title: string;
    reviewedCount: number;
    totalCount: number;
    headerActions?: React.ReactNode;
    className?: string;
};

const ReviewProgressCard = ({
    title,
    reviewedCount,
    totalCount,
    headerActions,
    className,
}: ReviewProgressCardProps) => {
    const safeTotal = totalCount > 0 ? totalCount : 0;
    const safeReviewed = Math.max(0, Math.min(reviewedCount, safeTotal));
    const notReviewedCount = Math.max(safeTotal - safeReviewed, 0);

    const reviewedPercent = safeTotal > 0 ? (safeReviewed / safeTotal) * 100 : 0;

    const reviewedDisplayPercent = bankersRound(reviewedPercent, 2);

    return (
        <Card
            data-testid="review-progress-card"
            className={cn(styles.card, className)}
            bodyClassName={styles.cardBody}
            bodyStyles={{ height: 'auto' }}
            title={title}
            headerActions={headerActions}
        >
            <div className={styles.percentLabel} data-testid="reviewed-percent-label">
                {reviewedDisplayPercent}% Reviewed
            </div>

            <div className={styles.progressWrapper}>
                <div
                    className={styles.progressBar}
                    role="progressbar"
                    aria-valuenow={safeReviewed}
                    aria-valuemin={0}
                    aria-valuemax={safeTotal}
                    aria-label={title}
                >
                    <div
                        className={cn(styles.progressSegment, styles.reviewed)}
                        style={{ width: `${reviewedPercent}%` }}
                        data-testid="progress-segment-reviewed"
                    />
                </div>
            </div>

            <div className={styles.legend}>
                <div className={styles.legendItem} data-testid="legend-reviewed">
                    <span className={cn(styles.legendSwatch, styles.reviewed)} />
                    <span className={styles.legendCount}>
                        <span className={styles.legendCount}>{safeReviewed}</span> Reviewed
                    </span>
                </div>
                <div className={styles.legendItem} data-testid="legend-not-reviewed">
                    <span className={cn(styles.legendSwatch, styles.notReviewed)} />
                    <span className={styles.legendCount}>
                        <span className={styles.legendCount}>{notReviewedCount}</span> Not Reviewed
                    </span>
                </div>
            </div>
        </Card>
    );
};

export default ReviewProgressCard;
